import { Hand, Mic, MicOff, Pin, PinOff, ShieldCheck } from 'lucide-react';

import { UserAvatar } from '@/components/user-avatar';

import { useEffect, useRef } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { safePlay } from './media';
import type { ParticipantTile } from './roomState';

interface VideoTileProps {
  readonly tile: ParticipantTile;
  /** True while this participant is an active speaker (speaking ring, both layouts). */
  readonly speaking?: boolean;
  /** True while this participant is explicitly pinned. */
  readonly pinned?: boolean;
  /** True while this participant's hand is raised (realtime channel state). */
  readonly handRaised?: boolean;
  /** True when this participant is a room admin (the host ShieldCheck on the nameplate). */
  readonly isAdmin?: boolean;
  /** Horizontally flips the video (own self-view with the "mirror video" option on). */
  readonly mirror?: boolean;
  /**
   * Fill mode (the dominant slot): the tile fills its parent box exactly (the parent is sized
   * by the grid math), dropping the standalone aspect-video frame. Default is the standalone
   * 16:9 card.
   */
  readonly fill?: boolean;
  /** Provided when the tile should offer the pin/unpin affordance. */
  readonly onTogglePin?: (() => void) | undefined;
}

/**
 * One participant tile in the Syncra call-screen look: rounded-xl frame over zinc-900, the
 * participant's video track attached to a muted <video> element (audio is played by
 * AudioSink), an avatar placeholder when the camera is off, and the bottom nameplate chip
 * (mic icon, «Вы»/name, amber host shield for admins) with the raised-hand badge at its
 * right. Active speakers get the pulsing emerald speaking-ring; a pin/unpin button appears
 * on hover when the layout supports pinning.
 */
export function VideoTile({
  tile,
  speaking = false,
  pinned = false,
  handRaised = false,
  isAdmin = false,
  mirror = false,
  fill = false,
  onTogglePin,
}: VideoTileProps): JSX.Element {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const track = tile.videoTrack;

  useEffect(() => {
    const element = videoRef.current;
    if (element === null || track === null) {
      return;
    }
    track.attach(element);
    safePlay(element);
    return () => {
      track.detach(element);
    };
  }, [track]);

  const displayName = tile.isLocal ? t('participants.you_display') : tile.label;

  return (
    <div
      data-testid={`tile-${tile.identity}`}
      className={cn(
        'group relative min-h-0 overflow-hidden rounded-xl bg-zinc-900',
        fill ? 'h-full w-full' : 'aspect-video',
        speaking ? 'speaking-ring' : 'ring-1 ring-white/[0.06]',
      )}
    >
      {track !== null ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={cn('h-full w-full object-cover', mirror && '-scale-x-100')}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-600 via-stone-600 to-zinc-800">
          <UserAvatar name={tile.label} size="xl" />
        </div>
      )}
      {speaking ? (
        <div
          data-testid={`active-speaker-ring-${tile.identity}`}
          aria-hidden
          className="pointer-events-none absolute inset-0 z-10 rounded-xl"
        />
      ) : null}
      {onTogglePin !== undefined ? (
        <button
          type="button"
          data-testid={`pin-${tile.identity}`}
          onClick={onTogglePin}
          title={pinned ? t('participants.unpin') : t('participants.pin')}
          aria-label={pinned ? t('participants.unpin') : t('participants.pin')}
          className={cn(
            'absolute right-2 top-2 z-20 grid h-7 w-7 place-items-center rounded-md bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/70',
            pinned ? 'text-emerald-300' : 'opacity-0 group-hover:opacity-100 focus:opacity-100',
          )}
        >
          {pinned ? <PinOff className="size-3.5" /> : <Pin className="size-3.5" />}
          <span className="sr-only">
            {pinned ? t('participants.unpin_short') : t('participants.pin_short')}
          </span>
        </button>
      ) : null}
      {/* nameplate + raised hand, per the Syncra tile: chip bottom-left, amber badge bottom-right */}
      <div className="absolute inset-x-2 bottom-2 z-20 flex items-center justify-between gap-2">
        <span className="inline-flex max-w-[75%] items-center gap-1.5 truncate rounded-md bg-black/55 px-2 py-1 text-[11.5px] font-medium text-white backdrop-blur-sm">
          {tile.micMuted ? (
            <MicOff className="size-3 shrink-0 text-red-400" />
          ) : (
            <Mic className="size-3 shrink-0 text-white/80" />
          )}
          <span className="truncate" data-testid={`tile-name-${tile.identity}`}>
            {displayName}
          </span>
          {isAdmin ? (
            <ShieldCheck
              className="size-3 shrink-0 text-amber-400"
              aria-label={t('participants.role_admin_title')}
            />
          ) : null}
        </span>
        {handRaised ? (
          <span
            data-testid={`hand-badge-${tile.identity}`}
            title={t('participants.hand_title')}
            aria-label={t('participants.hand_aria')}
            className="flex size-6 shrink-0 items-center justify-center rounded-md bg-amber-400 text-zinc-950 shadow-lg"
          >
            <Hand className="size-3.5" />
          </span>
        ) : null}
        <span
          data-testid={tile.micMuted ? `mic-muted-${tile.identity}` : `mic-on-${tile.identity}`}
          aria-label={tile.micMuted ? t('participants.mic_muted') : t('participants.mic_on')}
          title={tile.micMuted ? t('participants.mic_muted') : t('participants.mic_on')}
          className="sr-only"
        >
          {tile.micMuted ? t('participants.mic_muted') : t('participants.mic_on')}
        </span>
      </div>
    </div>
  );
}
